import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material'
import { cardsApi } from '@/api/cardsApi'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import type { Clue, WordClueSuggestion } from '@/types'

const CLUE_TEXT_MAX = 500

interface CardCluesProps {
  cardId: number
  wordId: number
  clues: Clue[]
  onClueAdded?: (clue: Clue) => void
  onClueDeleted?: (clueId: number) => void
  /**
   * Подсказка, которую нельзя удалять: у слова нет атрибута перевода, и она
   * работает и переводом на экране, и эталоном автопроверки на сервере.
   */
  protectedClueId?: number | null
  /** Пояснение под защищённой подсказкой. */
  protectedClueNote?: string
}

export function CardClues({
  cardId,
  wordId,
  clues,
  onClueAdded,
  onClueDeleted,
  protectedClueId = null,
  protectedClueNote,
}: CardCluesProps) {
  const { t } = useTranslation()
  const [newClueText, setNewClueText] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [deleteLoadingId, setDeleteLoadingId] = useState<number | null>(null)
  const [findLoading, setFindLoading] = useState(false)
  const [externalClues, setExternalClues] = useState<WordClueSuggestion[]>([])
  const [error, setError] = useState<string | null>(null)

  /**
   * Поколение карточки и поколение запроса «найти у других».
   *
   * Ответ по слову A, пришедший уже на слове B, подставлять нельзя: учитель
   * увидел бы чужие подсказки под другим словом и мог привязать их к нему.
   * По той же причине запрос, начатый на слове A, не должен снимать флаги
   * загрузки на слове B — иначе кнопка разблокируется под летящим запросом и
   * второй клик создаст дубль подсказки.
   */
  const cardGenerationRef = useRef(0)
  const findRequestRef = useRef(0)

  /**
   * Смена карточки очищает всё локальное состояние панели.
   *
   * В прогоне теста компонент монтируется с `key` по id карточки и там
   * пересоздаётся сам, но на странице карточки (`CardViewPage`) ключа нет и
   * инстанс переиспользуется: найденные у других учеников подсказки от прошлого
   * слова висели под новым вместе с недописанным текстом и старой ошибкой.
   * Опаснее мусора было то, что «Добавить» у застрявшей подсказки уходило с
   * текущим `cardId` — подсказка слова A привязывалась к карточке слова B.
   */
  useEffect(() => {
    // Новое поколение: летящий ответ по прошлому слову до состояния не доедет.
    cardGenerationRef.current += 1
    findRequestRef.current += 1
    setExternalClues([])
    setNewClueText('')
    setError(null)
    setFindLoading(false)
    setAddLoading(false)
    setDeleteLoadingId(null)
  }, [cardId, wordId])

  const handleAddClue = async () => {
    const text = newClueText.trim()
    if (!text) {
      setError(t('cards.validation.clue_text_required'))
      return
    }
    if (text.length > CLUE_TEXT_MAX) {
      setError(t('cards.validation.clue_text_max'))
      return
    }
    const generation = cardGenerationRef.current
    setError(null)
    setAddLoading(true)
    let clue: Clue
    try {
      clue = await cardsApi.addClue(cardId, text)
    } catch (err) {
      if (generation === cardGenerationRef.current) {
        setError(displayErrorMessage(getErrorMessage(err), t('cards.errors.clue_add_failed')))
        setAddLoading(false)
      }
      return
    }
    // Подсказка уже создана: сюда бросок родительского колбэка попасть не
    // должен, иначе пользователь увидит ошибку добавления и создаст дубль.
    if (generation === cardGenerationRef.current) {
      setNewClueText('')
      setAddLoading(false)
    }
    onClueAdded?.(clue)
  }

  const handleDeleteClue = async (clueId: number) => {
    if (!window.confirm(t('common.delete') + '?')) return
    const generation = cardGenerationRef.current
    setDeleteLoadingId(clueId)
    try {
      await cardsApi.deleteClue(cardId, clueId)
    } catch (err) {
      if (generation === cardGenerationRef.current) {
        setError(displayErrorMessage(getErrorMessage(err), t('cards.errors.clue_delete_failed')))
        setDeleteLoadingId(null)
      }
      return
    }
    // Подсказки уже нет: сообщаем родителю до сброса флага, чтобы строка
    // исчезла из списка раньше, чем корзина снова станет активной.
    onClueDeleted?.(clueId)
    if (generation === cardGenerationRef.current) setDeleteLoadingId(null)
  }

  const handleFindFromOthers = async () => {
    const generation = cardGenerationRef.current
    const request = ++findRequestRef.current
    setFindLoading(true)
    setError(null)
    setExternalClues([])
    try {
      const list = await cardsApi.getWordClues(wordId)
      if (request !== findRequestRef.current) return
      setExternalClues(list)
    } catch (err) {
      if (request !== findRequestRef.current) return
      setError(displayErrorMessage(getErrorMessage(err), t('cards.errors.clues_search_failed')))
    } finally {
      if (generation === cardGenerationRef.current && request === findRequestRef.current) {
        setFindLoading(false)
      }
    }
  }

  const handleAddExternalClue = async (text: string) => {
    const generation = cardGenerationRef.current
    setAddLoading(true)
    setError(null)
    let clue: Clue
    try {
      clue = await cardsApi.addClue(cardId, text)
    } catch (err) {
      if (generation === cardGenerationRef.current) {
        setError(displayErrorMessage(getErrorMessage(err), t('cards.errors.clue_add_failed')))
        setAddLoading(false)
      }
      return
    }
    if (generation === cardGenerationRef.current) {
      setExternalClues((prev) => prev.filter((c) => c.text !== text))
      setAddLoading(false)
    }
    onClueAdded?.(clue)
  }

  // Сравнение как в БД: колонка `clues.text` лежит в utf8mb4_unicode_ci, и
  // «Приветствие» с «приветствие» бэкенд считает одной подсказкой. Без этого
  // «Найти у других» предлагала добавить уже имеющуюся, создавая дубль.
  const clueKey = (text: string) => text.trim().toLocaleLowerCase()
  const currentClueTexts = new Set(clues.map((c) => clueKey(c.text)))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{t('cards.clues')}</Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <List dense>
        {clues.map((clue) => {
          const isProtected = protectedClueId != null && clue.id === protectedClueId
          return (
            <ListItem
              key={clue.id}
              secondaryAction={
                isProtected ? null : (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDeleteClue(clue.id)}
                    disabled={deleteLoadingId === clue.id}
                  >
                    {deleteLoadingId === clue.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                )
              }
            >
              <ListItemText
                primary={clue.text}
                secondary={isProtected ? (protectedClueNote ?? null) : null}
              />
            </ListItem>
          )
        })}
      </List>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={6}
          label={t('cards.clue_text')}
          value={newClueText}
          onChange={(e) => setNewClueText(e.target.value)}
          inputProps={{ maxLength: CLUE_TEXT_MAX }}
          helperText={`${newClueText.length}/${CLUE_TEXT_MAX}`}
          sx={{
            '& .MuiInputBase-root': { minHeight: 40, alignItems: 'flex-start' },
            '& .MuiInputBase-input': { minHeight: 22 },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleAddClue}
          disabled={addLoading || !newClueText.trim()}
          sx={{ height: 40, minHeight: 40 }}
        >
          {t('cards.add')}
        </Button>
      </Box>

      <Button
        variant="outlined"
        startIcon={findLoading ? <CircularProgress size={20} /> : <SearchIcon />}
        onClick={handleFindFromOthers}
        disabled={findLoading}
      >
        {t('cards.find_clues_from_other_students')}
      </Button>

      {externalClues.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('cards.messages.clues_found', { count: externalClues.length })}
          </Typography>
          <List dense>
            {externalClues.map((clue) => {
              const alreadyAdded = currentClueTexts.has(clueKey(clue.text))
              return (
                <ListItem
                  key={clue.id}
                  secondaryAction={
                    !alreadyAdded && (
                      <Button
                        size="small"
                        onClick={() => handleAddExternalClue(clue.text)}
                        disabled={addLoading}
                      >
                        {t('cards.add_clue')}
                      </Button>
                    )
                  }
                >
                  <ListItemText
                    primary={clue.text}
                    secondary={alreadyAdded ? t('cards.messages.clue_added') : null}
                  />
                </ListItem>
              )
            })}
          </List>
        </Box>
      )}
    </Box>
  )
}
